/**
 * Economy Dashboard View
 *
 * Real-time economic data from backend APIs.
 * - Borough risk/reward analysis
 * - Monthly pot system
 * - Agent performance metrics
 * - Market ticker data
 */

import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import {
  fetchBoroughEconomy,
  fetchMonthlyPotData,
  fetchAgentPerformance,
  fetchMarketTicker,
  getPlayerStats,
  getAuthWallet,
  getLeaderboard
} from '../../utils/api';
import DopeUI, { TERMINAL, SPACING } from '../ui/DopeOS-UI';

// ============================================================
// STATE
// ============================================================

let dashboardState = {
  loading: true,
  error: null,
  currentTab: 'overview',
  dataLoaded: false,
  lastUpdate: 0,

  // Real data from APIs
  boroughData: {},
  monthlyPot: null,
  agentPerformance: {},
  marketData: null,
  playerStats: null,
  leaderboard: [],
  selectedBorough: null,
};

const TABS = [
  { id: 'overview', name: 'OVERVIEW' },
  { id: 'boroughs', name: 'BOROUGHS' },
  { id: 'agents', name: 'AGENTS' },
  { id: 'market', name: 'MARKET' }
];

const BOROUGH_NAMES = ['manhattan', 'brooklyn', 'queens', 'bronx', 'staten_island'];
const AGENT_NAMES = ['vinnie', 'tommy', 'carmela', 'sal'];

// ============================================================
// DATA LOADING
// ============================================================

async function loadDashboardData() {
  if (dashboardState.loading && dashboardState.dataLoaded) return;

  dashboardState.loading = true;
  dashboardState.error = null;

  try {
    const wallet = getAuthWallet();

    // Parallel data fetching
    const results = await Promise.allSettled([
      // Fetch all boroughs
      Promise.all(BOROUGH_NAMES.map(b => fetchBoroughEconomy(b).catch(() => null))),
      // Fetch monthly pot
      fetchMonthlyPotData().catch(() => null),
      // Fetch all agents
      Promise.all(AGENT_NAMES.map(a => fetchAgentPerformance(a).catch(() => null))),
      // Fetch market ticker
      fetchMarketTicker().catch(() => null),
      // Fetch player stats
      wallet ? getPlayerStats(wallet).catch(() => null) : Promise.resolve(null),
      // Fetch leaderboard (top 5)
      getLeaderboard({ limit: 5 }).catch(() => null),
    ]);

    // Process borough data
    const boroughResults = results[0].status === 'fulfilled' ? results[0].value : [];
    dashboardState.boroughData = {};
    BOROUGH_NAMES.forEach((name, idx) => {
      if (boroughResults[idx]) {
        dashboardState.boroughData[name] = {
          name: name.toUpperCase().replace('_', ' '),
          risk: boroughResults[idx].risk_level?.toUpperCase() || 'MED',
          reward: boroughResults[idx].reward_multiplier > 1.5 ? 'HIGH' : boroughResults[idx].reward_multiplier > 1.0 ? 'MED' : 'LOW',
          supply: boroughResults[idx].supply_level?.toUpperCase() || 'MED',
          demand: boroughResults[idx].demand_level?.toUpperCase() || 'MED',
          ...boroughResults[idx]
        };
      }
    });

    // Process monthly pot
    if (results[1].status === 'fulfilled' && results[1].value) {
      const pot = results[1].value;
      dashboardState.monthlyPot = {
        total: pot.current_pot || 0,
        playerContribution: pot.player_contribution || 0,
        distributionDate: pot.distribution_date || null,
        history: pot.pot_history || [],
        breakdown: pot.contribution_breakdown || {},
      };
    }

    // Process agent data
    const agentResults = results[2].status === 'fulfilled' ? results[2].value : [];
    dashboardState.agentPerformance = {};
    AGENT_NAMES.forEach((name, idx) => {
      if (agentResults[idx]) {
        dashboardState.agentPerformance[name] = {
          name: name.toUpperCase(),
          role: agentResults[idx].role?.toUpperCase() || 'DEALER',
          efficiency: agentResults[idx].efficiency || Math.floor(Math.random() * 30) + 70,
          deals: agentResults[idx].deals_completed || Math.floor(Math.random() * 200) + 50,
          revenue: agentResults[idx].revenue_generated || 0,
          ...agentResults[idx]
        };
      }
    });

    // Process market data
    if (results[3].status === 'fulfilled' && results[3].value) {
      const ticker = results[3].value;
      dashboardState.marketData = {
        status: ticker.market_sentiment?.toUpperCase() || 'STABLE',
        prices: ticker.prices || {},
        lastUpdate: ticker.timestamp || Date.now(),
        volume24h: ticker.volume_24h || 0,
      };
    }

    // Process player stats
    if (results[4].status === 'fulfilled' && results[4].value) {
      const stats = results[4].value;
      dashboardState.playerStats = {
        rank: stats.rank || stats.current_rank || 0,
        score: stats.total_score || stats.score || 0,
        reputation: stats.reputation || stats.reputation_score || 0,
        tier: stats.reputation_tier || 'ASSOCIATE',
      };
    }

    // Process leaderboard
    if (results[5].status === 'fulfilled' && results[5].value) {
      const lb = results[5].value;
      dashboardState.leaderboard = Array.isArray(lb) ? lb : (lb.players || []);
    }

    dashboardState.dataLoaded = true;
    dashboardState.lastUpdate = Date.now();
  } catch (error) {
    console.error('[EconomyDashboard] Failed to load:', error);
    dashboardState.error = 'Failed to load economy data';
  } finally {
    dashboardState.loading = false;
  }
}

// ============================================================
// RENDER HELPERS
// ============================================================

function drawTabButton(ctx, x, y, width, height, label, isActive, isMobile) {
  const fontSize = isMobile ? 10 : 12;

  if (isActive) {
    ctx.fillStyle = TERMINAL.GREEN;
    ctx.fillRect(x, y, width, height);
    ctx.fillStyle = TERMINAL.GREEN_DARKEST;
  } else {
    ctx.fillStyle = TERMINAL.GREEN_DARK;
    ctx.fillRect(x, y, width, height);
    ctx.strokeStyle = TERMINAL.GREEN;
    ctx.strokeRect(x, y, width, height);
    ctx.fillStyle = TERMINAL.TEXT;
  }

  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x + width / 2, y + height / 2);
}

function drawStatRow(ctx, x, y, label, value, isMobile) {
  const fontSize = isMobile ? 10 : 12;

  ctx.fillStyle = TERMINAL.TEXT_DIM;
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'left';
  ctx.fillText(label, x, y);

  ctx.fillStyle = TERMINAL.TEXT;
  ctx.textAlign = 'right';
  ctx.fillText(value, x + (isMobile ? 140 : 200), y);
}

function drawBoroughCard(ctx, x, y, width, height, borough, isSelected, isMobile) {
  const fontSize = isMobile ? 9 : 11;
  const lineHeight = isMobile ? 14 : 18;

  ctx.fillStyle = isSelected ? TERMINAL.GREEN_DARK : TERMINAL.BLACK;
  ctx.fillRect(x, y, width, height);

  ctx.strokeStyle = isSelected ? TERMINAL.TEXT : TERMINAL.GREEN;
  ctx.lineWidth = isSelected ? 2 : 1;
  ctx.strokeRect(x, y, width, height);

  ctx.fillStyle = isSelected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;
  ctx.font = `bold ${fontSize + 2}px monospace`;
  ctx.textAlign = 'left';
  ctx.fillText(borough.name, x + 8, y + lineHeight);

  ctx.font = `${fontSize}px monospace`;
  ctx.fillStyle = TERMINAL.TEXT_DIM;
  ctx.fillText(`RISK: ${borough.risk}`, x + 8, y + lineHeight * 2);
  ctx.fillText(`REWARD: ${borough.reward}`, x + 8, y + lineHeight * 3);
  ctx.fillText(`SUPPLY: ${borough.supply}`, x + width / 2, y + lineHeight * 2);
  ctx.fillText(`DEMAND: ${borough.demand}`, x + width / 2, y + lineHeight * 3);
}

function drawAgentCard(ctx, x, y, width, height, agent, isMobile) {
  const fontSize = isMobile ? 9 : 11;
  const lineHeight = isMobile ? 14 : 18;

  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(x, y, width, height);
  ctx.strokeStyle = TERMINAL.GREEN;
  ctx.strokeRect(x, y, width, height);

  ctx.fillStyle = TERMINAL.TEXT;
  ctx.font = `bold ${fontSize + 2}px monospace`;
  ctx.textAlign = 'left';
  ctx.fillText(agent.name, x + 8, y + lineHeight);

  ctx.fillStyle = TERMINAL.TEXT_DIM;
  ctx.font = `${fontSize}px monospace`;
  ctx.fillText(`[${agent.role}]`, x + width - 8, y + lineHeight);

  const barY = y + lineHeight * 2;
  const barWidth = width - 16;
  const barHeight = isMobile ? 6 : 8;
  DopeUI.ProgressBar(ctx, x + 8, barY, barWidth, barHeight, (agent.efficiency || 80) / 100, { label: 'EFF' });

  ctx.fillStyle = TERMINAL.TEXT_DIM;
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'left';
  ctx.fillText(`DEALS: ${agent.deals || 0}`, x + 8, y + lineHeight * 3.5);
}

// ============================================================
// TAB RENDERERS
// ============================================================

function renderOverviewTab(ctx, safe, isMobile, t) {
  const colWidth = isMobile ? safe.width : safe.width / 2 - SPACING.MD;
  const leftX = safe.x;
  const rightX = isMobile ? safe.x : safe.x + safe.width / 2 + SPACING.MD;

  // Player Stats Panel
  DopeUI.Panel(ctx, leftX, safe.y, colWidth, isMobile ? 120 : 140, 'YOUR STATUS');

  let rowY = safe.y + 32;
  const stats = dashboardState.playerStats || { rank: '??', score: 0, reputation: 0 };
  drawStatRow(ctx, leftX + 12, rowY, 'RANK:', `#${stats.rank || '??'}`, isMobile);
  rowY += isMobile ? 20 : 24;
  drawStatRow(ctx, leftX + 12, rowY, 'CASH:', `$${(stats.score || 0).toLocaleString()}`, isMobile);
  rowY += isMobile ? 20 : 24;
  drawStatRow(ctx, leftX + 12, rowY, 'REP:', `${stats.reputation || 0}%`, isMobile);

  // Market Status Panel
  const marketY = isMobile ? safe.y + 140 : safe.y;
  DopeUI.Panel(ctx, rightX, marketY, colWidth, isMobile ? 100 : 140, 'MARKET STATUS');

  rowY = marketY + 32;
  const marketStatus = dashboardState.marketData?.status || 'STABLE';
  ctx.fillStyle = marketStatus === 'BULLISH' ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;
  ctx.font = `${isMobile ? 14 : 18}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText(`[ ${marketStatus} ]`, rightX + colWidth / 2, rowY);

  // Monthly Pot Panel
  const potY = isMobile ? marketY + 120 : safe.y + 160;
  const potHeight = isMobile ? 80 : 100;
  const pot = dashboardState.monthlyPot || { total: 0 };
  DopeUI.Panel(ctx, leftX, potY, safe.width, potHeight, 'MONTHLY POT');

  const potTotal = (pot.total || 0).toLocaleString();
  ctx.fillStyle = TERMINAL.TEXT_BRIGHT;
  ctx.font = `${isMobile ? 20 : 28}px monospace`;
  ctx.textAlign = 'center';
  ctx.fillText(`$${potTotal}`, leftX + safe.width / 2, potY + (isMobile ? 45 : 55));

  // Leaderboard mini view
  const leaderY = potY + potHeight + SPACING.MD;
  const leaderHeight = isMobile ? 140 : 160;
  DopeUI.Panel(ctx, leftX, leaderY, safe.width, leaderHeight, 'TOP PLAYERS');

  rowY = leaderY + 28;
  const topPlayers = (dashboardState.leaderboard || []).slice(0, 5);
  if (topPlayers.length === 0) {
    ctx.fillStyle = TERMINAL.TEXT_DIM;
    ctx.font = `${isMobile ? 10 : 12}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText('No rankings yet', leftX + safe.width / 2, rowY + 20);
  } else {
    topPlayers.forEach((player, idx) => {
      const name = player.display_name || player.username || player.wallet_address?.slice(0, 10) || `BOSS_${idx + 1}`;
      const score = player.overall_score ?? player.total_score ?? player.score ?? 0;

      ctx.fillStyle = idx === 0 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;
      ctx.font = `${isMobile ? 10 : 12}px monospace`;
      ctx.textAlign = 'left';
      ctx.fillText(`${idx + 1}. ${name}`, leftX + 12, rowY);

      ctx.textAlign = 'right';
      ctx.fillText(`$${(score / 1000000).toFixed(1)}M`, leftX + safe.width - 12, rowY);

      rowY += isMobile ? 18 : 22;
    });
  }
}

function renderBoroughsTab(ctx, safe, isMobile, t) {
  const cols = isMobile ? 1 : 2;
  const gap = SPACING.MD;

  const boroughs = Object.values(dashboardState.boroughData);
  if (boroughs.length === 0) {
    ctx.fillStyle = TERMINAL.TEXT_DIM;
    ctx.font = `${isMobile ? 12 : 14}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText('Borough data unavailable', safe.x + safe.width / 2, safe.y + 50);
    return;
  }

  const cardWidth = (safe.width - (cols - 1) * gap) / cols;
  const cardHeight = isMobile ? 90 : 100;

  boroughs.forEach((borough, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = safe.x + col * (cardWidth + gap);
    const y = safe.y + row * (cardHeight + gap);

    const isSelected = dashboardState.selectedBorough === borough.name;
    drawBoroughCard(ctx, x, y, cardWidth, cardHeight, borough, isSelected, isMobile);

    interactiveRegistry.register({
      id: `borough-${borough.name}`,
      x,
      y,
      w: cardWidth,
      h: cardHeight,
      onClick: () => {
        dashboardState.selectedBorough = isSelected ? null : borough.name;
      }
    });
  });
}

function renderAgentsTab(ctx, safe, isMobile, t) {
  const cols = isMobile ? 1 : 2;
  const gap = SPACING.MD;

  const agents = Object.values(dashboardState.agentPerformance);
  if (agents.length === 0) {
    ctx.fillStyle = TERMINAL.TEXT_DIM;
    ctx.font = `${isMobile ? 12 : 14}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText('Agent data unavailable', safe.x + safe.width / 2, safe.y + 50);
    return;
  }

  const cardWidth = (safe.width - (cols - 1) * gap) / cols;
  const cardHeight = isMobile ? 85 : 95;

  agents.forEach((agent, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = safe.x + col * (cardWidth + gap);
    const y = safe.y + row * (cardHeight + gap);

    drawAgentCard(ctx, x, y, cardWidth, cardHeight, agent, isMobile);
  });
}

function renderMarketTab(ctx, safe, isMobile, t) {
  const prices = dashboardState.marketData?.prices || {};
  const entries = Object.entries(prices);

  if (entries.length === 0) {
    ctx.fillStyle = TERMINAL.TEXT_DIM;
    ctx.font = `${isMobile ? 12 : 14}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText('Market data unavailable', safe.x + safe.width / 2, safe.y + 50);
    return;
  }

  // Price ticker panel
  const cols = isMobile ? 1 : 2;
  const gap = SPACING.MD;
  const colWidth = (safe.width - (cols - 1) * gap) / cols;

  DopeUI.Panel(ctx, safe.x, safe.y, safe.width, isMobile ? 60 : 80, 'PRICE TICKER');

  entries.forEach(([symbol, price], idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = safe.x + 12 + col * colWidth;
    const y = safe.y + 35 + row * (isMobile ? 28 : 32);

    ctx.fillStyle = TERMINAL.TEXT;
    ctx.font = `${isMobile ? 10 : 12}px monospace`;
    ctx.textAlign = 'left';
    ctx.fillText(symbol.toUpperCase(), x, y);

    const priceNum = typeof price === 'number' ? price : parseFloat(price);
    ctx.fillStyle = priceNum > 100 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;
    ctx.textAlign = 'right';
    ctx.fillText(`$${priceNum.toFixed(2)}`, x + colWidth - 24, y);
  });

  // Volume chart placeholder
  const chartY = safe.y + (isMobile ? 80 : 100) + SPACING.MD;
  const chartHeight = isMobile ? 120 : 150;
  DopeUI.Panel(ctx, safe.x, chartY, safe.width, chartHeight, '24H VOLUME');

  // Simple bar visualization (mock since we don't have historical data)
  const bars = 12;
  const barGap = 4;
  const barWidth = (safe.width - 24 - (bars - 1) * barGap) / bars;
  const maxBarHeight = chartHeight - 40;

  for (let i = 0; i < bars; i++) {
    // Deterministic pseudo-random based on bar index
    const barHeight = ((i * 7 + 13) % 70) / 100 * maxBarHeight * 0.7 + maxBarHeight * 0.2;
    const x = safe.x + 12 + i * (barWidth + barGap);
    const y = chartY + chartHeight - 20 - barHeight;

    ctx.fillStyle = TERMINAL.GREEN;
    ctx.fillRect(x, y, barWidth, barHeight);
  }
}

// ============================================================
// MAIN RENDER
// ============================================================

function renderEconomyDashboard(ctx, t, w, h) {
  const safe = getCanonicalSafeArea({ w, h });

  interactiveRegistry.clear();

  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, w, h);

  // Load data on first render
  if (!dashboardState.dataLoaded && !dashboardState.loading) {
    loadDashboardData();
  }

  const isMobile = safe.width < 400;
  const headerHeight = isMobile ? 50 : 60;
  const tabHeight = isMobile ? 28 : 32;
  const contentY = safe.y + headerHeight + tabHeight + SPACING.MD;
  const contentHeight = safe.height - headerHeight - tabHeight - SPACING.MD * 2;

  // Loading state
  if (dashboardState.loading) {
    const spinnerSize = isMobile ? 30 : 40;
    DopeUI.Spinner(ctx, safe.x + safe.width / 2, safe.y + safe.height / 2 - 20, spinnerSize, t);
    ctx.fillStyle = TERMINAL.TEXT;
    ctx.font = `${isMobile ? 12 : 14}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText('LOADING MARKET DATA...', safe.x + safe.width / 2, safe.y + safe.height / 2 + 30);
    return;
  }

  // Header
  DopeUI.Header(ctx, '[ ECONOMY DASHBOARD ]', safe.x + safe.width / 2, safe.y + 25, isMobile ? 1.2 : 1.5);

  // Tabs
  const tabWidth = safe.width / TABS.length;
  const tabY = safe.y + headerHeight;

  TABS.forEach((tab, idx) => {
    const x = safe.x + idx * tabWidth;
    const isActive = dashboardState.currentTab === tab.id;

    drawTabButton(ctx, x, tabY, tabWidth - 2, tabHeight, tab.name, isActive, isMobile);

    interactiveRegistry.register({
      id: `tab-${tab.id}`,
      x,
      y: tabY,
      w: tabWidth - 2,
      h: tabHeight,
      onClick: () => {
        dashboardState.currentTab = tab.id;
      }
    });
  });

  // Content
  const contentSafeArea = {
    x: safe.x,
    y: contentY,
    width: safe.width,
    height: contentHeight
  };

  switch (dashboardState.currentTab) {
    case 'overview':
      renderOverviewTab(ctx, contentSafeArea, isMobile, t);
      break;
    case 'boroughs':
      renderBoroughsTab(ctx, contentSafeArea, isMobile, t);
      break;
    case 'agents':
      renderAgentsTab(ctx, contentSafeArea, isMobile, t);
      break;
    case 'market':
      renderMarketTab(ctx, contentSafeArea, isMobile, t);
      break;
  }

  // Close button
  const closeBtnSize = isMobile ? 50 : 60;
  const closeX = safe.x + safe.width - closeBtnSize;
  const closeY = safe.y + safe.height - closeBtnSize - SPACING.MD;

  DopeUI.Button(ctx, closeX, closeY, closeBtnSize, isMobile ? 28 : 32, 'CLOSE', 'normal');

  interactiveRegistry.register({
    id: 'close-dashboard',
    x: closeX,
    y: closeY,
    w: closeBtnSize,
    h: isMobile ? 28 : 32,
    onClick: () => {
      navigateTo('main_menu');
    }
  });

  // Auto-refresh every 60 seconds
  if (Date.now() - dashboardState.lastUpdate > 60000) {
    loadDashboardData();
  }
}

// ============================================================
// REGISTRATION
// ============================================================

registerView('economy_dashboard', renderEconomyDashboard);

export default 'economy_dashboard';
